import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrandButton } from "@/components/PageHeader";
import { api, cedi2 as cedi, errMsg, fmtDate } from "@/lib/api";

export const available = (t) => Math.max(Math.round(((t.amount || 0) - (t.paid || 0) - (t.pending_cheque || 0)) * 100) / 100, 0);

export const EMPTY_CHEQUE = { cheque_no: "", bank: "", issuer: "" };
export const chequeOk = (c) => c.cheque_no.trim().length >= 3 && c.bank.trim().length >= 2 && c.issuer.trim().length >= 2;

const Field = ({ label, children }) => <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-gray-600">{label}</span><div className="mt-1.5">{children}</div></label>;

export const ChequeFields = ({ c, set }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-[#E5E7EB] p-3 bg-[#F8F9FA]" data-testid="cheque-fields">
    <Field label="Cheque number"><Input value={c.cheque_no} maxLength={40} onChange={(e) => set({ ...c, cheque_no: e.target.value })} className="h-10 font-mono bg-white" data-testid="cheque-number-input" /></Field>
    <Field label="Bank"><Input value={c.bank} maxLength={100} onChange={(e) => set({ ...c, bank: e.target.value })} placeholder="e.g. GCB Bank" className="h-10 bg-white" data-testid="cheque-bank-input" /></Field>
    <div className="sm:col-span-2"><Field label="Issuer (name on the receipt)"><Input value={c.issuer} maxLength={150} onChange={(e) => set({ ...c, issuer: e.target.value })} placeholder="Person or organisation that wrote the cheque" className="h-10 bg-white" data-testid="cheque-issuer-input" /></Field></div>
    <p className="sm:col-span-2 text-[11px] text-gray-600">A receipt is issued now. The registration counts as paid only after you mark the cheque cleared.</p>
  </div>
);

export const postCheque = async (c, shares) => (await api.post("/cheques", { ...c, shares })).data;

const ShareRow = ({ t, value, onChange }) => (
  <tr data-testid={`group-cheque-row-${t.ticket_no}`}>
    <td className="py-1.5 pr-2"><div className="font-medium">{t.delegate_name}</div><div className="text-[11px] text-gray-500 font-mono">{t.ticket_no} · balance {cedi(available(t))}</div></td>
    <td className="py-1.5 w-32"><Input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 font-mono" data-testid={`group-cheque-amount-${t.ticket_no}`} /></td>
  </tr>
);

const GroupForm = ({ rows, onDone }) => {
  const [c, setC] = useState(EMPTY_CHEQUE);
  const [amounts, setAmounts] = useState(() => Object.fromEntries(rows.map((t) => [t.id, available(t).toFixed(2)])));
  const [busy, setBusy] = useState(false);
  const total = rows.reduce((s, t) => s + (Number(amounts[t.id]) || 0), 0);
  const bad = rows.some((t) => !(Number(amounts[t.id]) > 0) || Number(amounts[t.id]) > available(t));
  const save = async () => {
    setBusy(true);
    try { const r = await postCheque(c, rows.map((t) => ({ txn_id: t.id, amount: Number(amounts[t.id]) }))); toast.success(`Cheque recorded · receipt ${r.receipt_no}`); onDone(r); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4 text-sm">
      <ChequeFields c={c} set={setC} />
      <div className="max-h-64 overflow-y-auto"><table className="w-full"><tbody className="divide-y">{rows.map((t) => <ShareRow key={t.id} t={t} value={amounts[t.id]} onChange={(v) => setAmounts({ ...amounts, [t.id]: v })} />)}</tbody></table></div>
      {bad && <p className="text-xs text-[#E60000]" data-testid="group-cheque-bad">Each amount must be more than zero and no more than that person's balance.</p>}
      <div className="flex justify-between font-semibold"><span>Cheque total</span><span className="font-mono" data-testid="group-cheque-total">{cedi(total)}</span></div>
      <BrandButton className="w-full h-11" disabled={busy || bad || !chequeOk(c)} onClick={save} data-testid="group-cheque-submit">{busy ? "Recording…" : `Record cheque of ${cedi(total)}`}</BrandButton>
    </div>
  );
};

export const GroupChequeDialog = ({ rows, onClose, onDone }) => (
  <Dialog open={!!rows} onOpenChange={onClose}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-cheque-dialog">
      <DialogHeader><DialogTitle className="font-heading">One cheque for {rows?.length} participant(s)</DialogTitle></DialogHeader>
      {rows && <GroupForm rows={rows} onDone={onDone} />}
    </DialogContent>
  </Dialog>
);

const STATUS_STYLE = { Pending: "bg-amber-50 text-amber-800", Cleared: "bg-emerald-50 text-emerald-800", Bounced: "bg-red-50 text-red-800" };
export const ChequeStatus = ({ s }) => <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[s]}`}>{s === "Pending" ? "Waiting to clear" : s}</span>;

export const ChequeReceipt = ({ c }) => (
  <div className="print-area bg-white text-[#111]" data-testid="cheque-receipt">
    <div className="flex items-center gap-3 border-b-4 border-[#E60000] pb-4">
      <img src="/images/gamls-logo.png" alt="GAMLS" className="w-14 h-14" />
      <div><div className="font-heading font-extrabold">Ghana Association of Medical Laboratory Scientists</div><div className="text-xs text-gray-500">Official Payment Receipt · Cheque</div></div>
    </div>
    <div className="grid grid-cols-2 gap-y-2 text-sm mt-4">
      <span className="text-gray-500">Receipt No.</span><span className="font-mono font-semibold" data-testid="cheque-receipt-no">{c.receipt_no}</span>
      <span className="text-gray-500">Received from</span><span className="font-semibold" data-testid="cheque-receipt-issuer">{c.issuer}</span>
      <span className="text-gray-500">Cheque No.</span><span className="font-mono">{c.cheque_no}</span>
      <span className="text-gray-500">Bank</span><span>{c.bank}</span>
      <span className="text-gray-500">Date</span><span>{fmtDate(c.created_at)}</span>
      <span className="text-gray-500">Cheque status</span><span><ChequeStatus s={c.status} /></span>
    </div>
    <table className="w-full text-xs mt-4" data-testid="cheque-receipt-shares">
      <thead className="text-gray-500"><tr>{["Ticket", "Participant", "Event", "Amount"].map((h) => <th key={h} className="text-left font-semibold py-1">{h}</th>)}</tr></thead>
      <tbody className="divide-y">{c.shares.map((s) => <tr key={s.txn_id}><td className="py-1 font-mono">{s.ticket_no}</td><td>{s.delegate_name}</td><td>{s.event_title}</td><td className="font-mono">{cedi(s.amount)}</td></tr>)}</tbody>
    </table>
    <div className="flex justify-between mt-4 pt-3 border-t font-heading text-lg font-extrabold"><span>Total</span><span data-testid="cheque-receipt-total">{cedi(c.amount)}</span></div>
    {c.status === "Pending" && <p className="mt-2 text-[11px] text-gray-600">Payment is confirmed once the cheque clears.</p>}
    <div className="mt-5 bg-[#E60000] text-white text-center text-xs font-bold tracking-[0.2em] py-2">SERVICE TO HUMANITY</div>
  </div>
);
